import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";

/**
 * ResourceCard: プラン・部屋一覧の mobile カード（120×120 サムネイル + 右テキスト）
 *
 * ゲスト側 app/frontend/guest/plain/src/components/top/ResourceCard.astro の逐語再現。
 * 値を変更する場合は必ずゲスト側も確認・同期すること。
 */

type Props = {
	imageUrl: string;
	title: string;
	/** mainTarget（プラン）または catchCopy（部屋） */
	catchCopy?: string;
	/** badge（例: 日帰りプラン） */
	badge?: string;
	/** 料金ラベル（例: 直近 7 日間） */
	priceLabel?: string;
	/** 料金テキスト（例: ￥20,000~） */
	priceText?: string;
	/** 料金単位（例: 名・日） */
	priceUnit?: string;
	className?: string;
};

export const ResourceCard = memo(
	({ imageUrl, title, catchCopy, badge, priceLabel, priceText, priceUnit, className }: Props) => {
		return (
			<div className={cn("flex gap-4 overflow-hidden", className)}>
				<div className="relative size-[120px] shrink-0 overflow-hidden rounded-lg">
					{imageUrl ? (
						<img
							src={imageUrl}
							alt={title}
							width="640"
							height="360"
							loading="lazy"
							className="size-full object-cover"
						/>
					) : (
						<div className="size-full bg-zinc-100" />
					)}
					{badge && (
						<span className="absolute right-1 top-1 w-fit rounded bg-black/60 px-1 py-0.5 text-[0.625rem] font-semibold text-white">
							{badge}
						</span>
					)}
				</div>
				<div className="flex flex-1 flex-col justify-between py-3 pr-3">
					<div className="flex flex-col gap-1">
						{catchCopy && <span className="line-clamp-1 text-xs">{catchCopy}</span>}
						<p className="line-clamp-2 text-sm font-bold text-zinc-900">{title}</p>
					</div>
					{priceText && (
						<div className="mt-1 text-end">
							{priceLabel && <div className="text-[0.625rem]">{priceLabel}</div>}
							<div className="flex items-baseline justify-end gap-0.5 text-sm font-medium text-zinc-900">
								<span>{priceText}</span>
								{priceUnit && <span className="text-[0.625rem]">/{priceUnit}</span>}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	},
);

ResourceCard.displayName = "ResourceCard";
